import React, { useRef, useState, useEffect, useCallback } from 'react';
import { bool, object, func, array } from 'prop-types';
import { translate } from 'react-i18next';
import { connect } from 'react-redux';

import { Button, UploadIcon } from '@snapask/common-components';
import LineInput from '@dragonfruit/components/fields/LineInput';
import LineSelect from '@dragonfruit/components/fields/LineSelect';

import {
  getBankInfoData,
  getBankInfoFields,
  fetchBankList,
  getBankList,
  getOpenFromStageLink,
  patchBankInfo,
} from '@dragonfruit/redux/bank';

import { camelcaseParser } from '@snapask/common-components/dist/utils/objCamelcaseParser';
import { IMAGE_FILE_TYPE } from '@dragonfruit/constants/fileType';
import { BANK_NAME, PASSBOOK_PIC } from '@dragonfruit/constants/bankField';

import styles from './EditBankInfoForm.scss';

const cx = require('classnames/bind').bind(styles);
const mapStateToProps = state => ({
  bankInfoFields: getBankInfoFields(state),
  bankInfoData: getBankInfoData(state),
  bankList: getBankList(state),
  openFromStageLink: getOpenFromStageLink(state),
});

const CAMEL_PASSBOOK_PIC = camelcaseParser(PASSBOOK_PIC);

function EditBankInfoForm({
  t,
  bankInfoFields,
  bankInfoData,
  fetchBankList,
  bankList,
  patchBankInfo,
  openFromStageLink,
}) {
  const [allowSubmit, setAllowSubmit] = useState(false);
  const [passBookImage, setPassBookImage] = useState({
    url: bankInfoData[CAMEL_PASSBOOK_PIC],
    file: null,
  });
  const [userBankData, setUserBankData] = useState({ ...bankInfoData });

  const bankInfoRef = useRef(null);
  const bankTextFields = useRef(
    bankInfoFields.filter(field => field !== PASSBOOK_PIC)
  );

  useEffect(() => {
    fetchBankList();
  }, [fetchBankList]);

  useEffect(() => {
    if (openFromStageLink && bankInfoRef.current) {
      const { offsetTop } = bankInfoRef.current;
      const { top } = bankInfoRef.current.getBoundingClientRect();
      window.scrollTo({
        top: top - offsetTop,
        behavior: 'smooth',
      });
    }
  }, [openFromStageLink]);

  useEffect(() => {
    checkAllowSubmit();
  }, [checkAllowSubmit, userBankData]);

  const onFieldChange = field => value => {
    setUserBankData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const onBankNameChange = field => option => {
    if (!option) return;

    setUserBankData(prev => ({
      ...prev,
      [field]: option.bankName,
    }));
  };

  const onFileChange = event => {
    const [selectedFile] = event.target.files;

    if (!selectedFile.type.includes(IMAGE_FILE_TYPE)) return;

    setPassBookImage({
      url: URL.createObjectURL(selectedFile),
      file: selectedFile,
    });
  };

  const checkAllowSubmit = useCallback(() => {
    const allFieldsFilled = bankTextFields.current.every(
      field => !!userBankData[camelcaseParser(field)]
    );
    setAllowSubmit(allFieldsFilled);
  }, [userBankData]);

  const onBankInfoSubmit = () => {
    patchBankInfo({
      ...userBankData,
      [CAMEL_PASSBOOK_PIC]: passBookImage,
    });
  };

  const renderBankFields = field => {
    const camelField = camelcaseParser(field);

    if (field === BANK_NAME)
      return (
        <LineSelect
          key={field}
          className={styles.bankNameSelector}
          floatingLabelText={t(`fieldLabel:fieldLabel.bank.${field}`)}
          placeholder={t('fieldLabel:fieldLabel.bank.bank_name_placeholder')}
          options={bankList}
          labelKey="bankName"
          valueKey="bankName"
          clearable={false}
          onChange={onBankNameChange(camelField)}
          value={userBankData[camelField]}
          searchable
          creatable
          promptTextCreator={label =>
            t('fieldLabel:fieldLabel.bank.bank_name_create_label', {
              name: label,
            })
          }
          openOnFocus
          openOnClick
        />
      );

    return (
      <LineInput
        key={field}
        value={userBankData[camelField]}
        floatingLabelText={t(`fieldLabel:fieldLabel.bank.${field}`)}
        onChange={onFieldChange(camelField)}
      />
    );
  };

  return (
    <div ref={bankInfoRef}>
      {bankTextFields.current.map(renderBankFields)}
      {bankInfoFields.includes(PASSBOOK_PIC) && (
        <label
          className={cx(styles.upload, { hasImage: !!passBookImage.url })}
          style={{
            backgroundImage: !!passBookImage.url && `url(${passBookImage.url})`,
          }}
        >
          <div className={styles.uploadHint}>
            <UploadIcon className={styles.uploadIcon} width={24} height={24} />
            <p className={styles.uploadText}>
              {t('fieldLabel:fieldLabel.bank.passbook_pic')}
            </p>
          </div>
          <input
            type="file"
            className={styles.uploadInput}
            onChange={onFileChange}
          />
        </label>
      )}
      <Button
        text={t('account.save')}
        disabled={!allowSubmit}
        primary
        onClick={onBankInfoSubmit}
      />
    </div>
  );
}
EditBankInfoForm.propTypes = {
  bankInfoData: object.isRequired,
  bankInfoFields: array.isRequired,
  bankList: array.isRequired,
  fetchBankList: func.isRequired,
  patchBankInfo: func.isRequired,
  t: func.isRequired,
  openFromStageLink: bool,
};

export default connect(mapStateToProps, { fetchBankList, patchBankInfo })(
  translate('account', 'fieldLabel')(EditBankInfoForm)
);



// WEBPACK FOOTER //
// ./app/containers/Account/children/AccountProfile/components/AccountProfileBankInfo/components/EditBankInfoForm/EditBankInfoForm.jsx